// @tennis-matches/services/winterdoppel/bonds

import { getSupabaseClient } from "../supabase/index.js";
import { partnerHistoryKey } from "./pairing.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

const BOND_SELECT =
  "id,tenant_id,created_by,series_id,player_a_id,player_b_id,created_at,updated_at";

/** Ordered uuid pair for DB constraint player_a < player_b (text compare). */
export function orderWinterDoppelBondPlayerIds(playerIdA, playerIdB) {
  const a = __asStr(playerIdA);
  const b = __asStr(playerIdB);
  if (!a || !b || a === b) return null;
  return a < b ? [a, b] : [b, a];
}

/** @returns {Array<[string, string]>} */
export function bondsToForcedPairs(bonds = []) {
  const pairs = [];
  for (const bond of bonds || []) {
    const ordered = orderWinterDoppelBondPlayerIds(
      bond.player_a_id || bond.playerAId,
      bond.player_b_id || bond.playerBId,
    );
    if (ordered) pairs.push(ordered);
  }
  return pairs;
}

export function partnerIdFromBonds(bonds = [], playerId) {
  const ids = partnerIdsFromBonds(bonds, playerId);
  return ids[0] || null;
}

/** Alle gewünschten Partner eines Spielers (stabil sortiert). */
export function partnerIdsFromBonds(bonds = [], playerId) {
  const pid = __asStr(playerId);
  if (!pid) return [];
  const out = [];
  const seen = new Set();
  for (const bond of bonds || []) {
    const a = __asStr(bond.player_a_id || bond.playerAId);
    const b = __asStr(bond.player_b_id || bond.playerBId);
    let other = null;
    if (a === pid) other = b;
    else if (b === pid) other = a;
    if (!other || seen.has(other)) continue;
    seen.add(other);
    out.push(other);
  }
  return out.sort((x, y) => x.localeCompare(y));
}

export async function loadWinterDoppelPlayerBonds(seriesId) {
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_player_bonds")
    .select(BOND_SELECT)
    .eq("series_id", __asStr(seriesId));
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

/**
 * Setzt den festen Partner für einen Spieler (ersetzt bestehende Bonds dieses Spielers).
 * partnerId null → Bond entfernen.
 */
export async function setWinterDoppelPlayerBond(
  seriesId,
  playerId,
  partnerId,
  tenantId = null,
) {
  const sid = __asStr(seriesId);
  const pid = __asStr(playerId);
  const partner = __asStr(partnerId);
  if (!sid || !pid) throw new Error("winterdoppel.bond.invalid");

  const supabase = await getSupabaseClient();
  const existing = await loadWinterDoppelPlayerBonds(sid);
  const toDelete = existing
    .filter(
      (b) =>
        b.player_a_id === pid ||
        b.player_b_id === pid ||
        (partner && (b.player_a_id === partner || b.player_b_id === partner)),
    )
    .map((b) => b.id)
    .filter(Boolean);

  if (toDelete.length) {
    const { error: delErr } = await supabase
      .from("winterdoppel_player_bonds")
      .delete()
      .in("id", toDelete);
    if (delErr) throw delErr;
  }

  if (!partner || partner === pid) {
    return loadWinterDoppelPlayerBonds(sid);
  }

  const ordered = orderWinterDoppelBondPlayerIds(pid, partner);
  if (!ordered) throw new Error("winterdoppel.bond.invalid");

  const row = {
    series_id: sid,
    player_a_id: ordered[0],
    player_b_id: ordered[1],
  };
  const tid = __asStr(tenantId);
  if (tid) row.tenant_id = tid;

  const { error: insErr } = await supabase
    .from("winterdoppel_player_bonds")
    .insert(row);
  if (insErr) throw insErr;
  return loadWinterDoppelPlayerBonds(sid);
}

export async function deleteWinterDoppelPlayerBond(bondId) {
  const supabase = await getSupabaseClient();
  const { error } = await supabase
    .from("winterdoppel_player_bonds")
    .delete()
    .eq("id", __asStr(bondId));
  if (error) throw error;
}

/** Partnerwunsch anlegen (mehrere pro Spieler erlaubt). */
export async function addWinterDoppelPlayerBond(
  seriesId,
  playerAId,
  playerBId,
  tenantId = null,
) {
  const sid = __asStr(seriesId);
  const ordered = orderWinterDoppelBondPlayerIds(playerAId, playerBId);
  if (!sid || !ordered) throw new Error("winterdoppel.bond.invalid");

  const existing = await loadWinterDoppelPlayerBonds(sid);
  const same = existing.find(
    (b) => b.player_a_id === ordered[0] && b.player_b_id === ordered[1],
  );
  if (same) return existing;

  const supabase = await getSupabaseClient();
  const row = {
    series_id: sid,
    player_a_id: ordered[0],
    player_b_id: ordered[1],
  };
  const tid = __asStr(tenantId);
  if (tid) row.tenant_id = tid;
  const { error } = await supabase
    .from("winterdoppel_player_bonds")
    .insert(row);
  if (error) throw error;
  return loadWinterDoppelPlayerBonds(sid);
}

/** Für späteren TrainingsDesk-Import: stabile Serien-/Spieler-/Bond-Snapshot. */
export function buildWinterDoppelSeriesExportPayload({
  series,
  players = [],
  bonds = [],
  groups = [],
  sessions = [],
  sessionPlayers = [],
} = {}) {
  const spsBySession = new Map();
  for (const sp of sessionPlayers || []) {
    const sid = String(sp?.session_id || "");
    if (!sid) continue;
    const list = spsBySession.get(sid) || [];
    list.push({
      playerId: sp.player_id,
      role: sp.role,
      rsvp: sp.rsvp,
      courtNo: sp.court_no ?? null,
      pairSlot: sp.pair_slot ?? null,
      costShare: Number(sp.cost_share) || 0,
    });
    spsBySession.set(sid, list);
  }

  return {
    source: "winterdoppel",
    version: 1,
    exportedAt: new Date().toISOString(),
    series: series
      ? {
          id: series.id,
          name: series.name,
          seriesKind: series.series_kind || series.seriesKind || "winter",
          seriesMode: series.series_mode || series.seriesMode || "free",
          seasonStart: series.season_start || series.seasonStart,
          seasonEnd: series.season_end || series.seasonEnd,
          weekdays: series.weekdays,
          startTime: series.start_time || series.startTime,
          endTime: series.end_time || series.endTime,
          courtCount: series.court_count || series.courtCount,
          planStatus: series.plan_status || series.planStatus || "draft",
        }
      : null,
    players: (players || []).map((p) => ({
      id: p.id,
      displayName: p.display_name || p.displayName,
      clubPersonId: p.club_person_id || p.clubPersonId || null,
      phone: p.phone || null,
      interestStatus: p.interest_status || p.interestStatus,
      weekRule: p.week_rule || p.weekRule,
    })),
    bonds: bondsToForcedPairs(bonds).map(([a, b]) => ({
      playerIds: [a, b],
      key: partnerHistoryKey(a, b),
    })),
    groups: (groups || []).map((g) => ({
      id: g.id,
      name: g.name,
      kind: g.kind,
      intervalWeeks: g.interval_weeks ?? g.intervalWeeks,
      weekday: g.weekday,
      startTime: g.start_time || g.startTime,
      endTime: g.end_time || g.endTime,
      scheduleStart: g.schedule_start || g.scheduleStart,
      scheduleEnd: g.schedule_end || g.scheduleEnd,
      holidaySubdivision: g.holiday_subdivision || g.holidaySubdivision,
      excludeHolidays: g.exclude_holidays !== false,
      playerIds: g.playerIds || (g.members || []).map((m) => m.player_id),
    })),
    sessions: (sessions || [])
      .filter((s) => s && s.status !== "cancelled")
      .map((s) => ({
        id: s.id,
        sessionDate: s.session_date || s.sessionDate,
        status: s.status,
        players: spsBySession.get(String(s.id)) || [],
      })),
  };
}
